import { ISaveManager, SavedData, SaveManagerId } from './ISaveManager';
import { isAuthenticated } from '../api/authManager';
import { apiClient } from '../api/apiClient';
import { logger } from '../Analytics/Logger';

/**
 * CloudSaveManager - saves game data to DynamoDB via Lambda
 * Only enabled when user is authenticated
 */
export class CloudSaveManager<TGameData = any> implements ISaveManager<TGameData> {
  readonly id = SaveManagerId.Cloud;
  private loadCache: Map<string, { savedGameData: SavedData<TGameData> | null; timestamp: number }> = new Map();
  private readonly CACHE_TTL = 5000; // Cache for 5 seconds
  
  // Batching for save requests - debounce saves to reduce API calls
  private pendingSaves: Map<string, {
    savedGameData: TGameData;
    timestamp: number;
    deviceId: string;
    timeoutHandle: ReturnType<typeof setTimeout>;
  }> = new Map();
  private readonly SAVE_DEBOUNCE_MS = 2000; // Batch saves within 2 seconds
  
  // Timeout for cloud operations - shorter than default to avoid blocking offline users
  private readonly CLOUD_TIMEOUT_MS = 5000; // 5 seconds for save/load operations

  constructor() {}

  /**
   * Cloud save enabled when user is authenticated
   */
  isEnabled(): boolean {
    return isAuthenticated();
  }

  /**
   * Save game data to cloud via Lambda
   * Batches saves within 2 seconds to reduce API calls
   * Note: expirationDate parameter is ignored for cloud saves (cloud data persists indefinitely)
   */
  async save(gameId: string, playerId: string, savedGameData: TGameData, timestamp: number, deviceId: string, expirationDate?: Date): Promise<void> {
    if (!this.isEnabled()) {
      logger.warn('[CloudSaveManager] Cannot save - user not authenticated');
      return;
    }

    const cacheKey = `${gameId}_${playerId}`;
    
    // Clear any existing pending save timeout
    const existingPending = this.pendingSaves.get(cacheKey);
    if (existingPending) {
      clearTimeout(existingPending.timeoutHandle);
      logger.debug(`[CloudSaveManager] Batching save request for ${gameId} (will save in ${this.SAVE_DEBOUNCE_MS} ms)`);
    }

    // Create new pending save with debounced execution
    const timeoutHandle = setTimeout(async () => {
      const pending = this.pendingSaves.get(cacheKey);
      if (!pending) return; // Already processed or cancelled
      
      this.pendingSaves.delete(cacheKey);
      
      try {
        const payload = {
          gameId,
          playerId,
          saveData: pending.savedGameData,
          timestamp: pending.timestamp,
          deviceId: pending.deviceId,
        };

        await apiClient('/sync/save-game-data', {
          method: 'POST',
          body: JSON.stringify(payload)
        }, this.CLOUD_TIMEOUT_MS);
        
        logger.debug(`[CloudSaveManager] Saved ${gameId} for player ${playerId} to cloud (device: ${pending.deviceId})`);
        
        // Clear cache after save since data changed
        this.loadCache.delete(cacheKey);
      } catch (error) {
        logger.error('[CloudSaveManager] Failed to save to cloud:', error);
        // Don't throw - save has already been batched/delayed
      }
    }, this.SAVE_DEBOUNCE_MS);

    // Store the pending save
    this.pendingSaves.set(cacheKey, {
      savedGameData,
      timestamp,
      deviceId,
      timeoutHandle
    });
  }

  /**
   * Load game data from DynamoDB via Lambda
   */
  async load(gameId: string, playerId: string): Promise<SavedData<TGameData> | null> {
    if (!this.isEnabled()) {
      logger.warn('[CloudSaveManager] Cannot load - user not authenticated');
      return null;
    }

    const cacheKey = `${gameId}_${playerId}`;
    
    // Check cache first
    const cached = this.loadCache.get(cacheKey);
    if (cached && (Date.now() - cached.timestamp) < this.CACHE_TTL) {
      logger.debug(`[CloudSaveManager] Using cached cloud data for ${gameId}`);
      return cached.savedGameData;
    }

    try {
      const response = await apiClient(`/sync/load-game-data?gameId=${gameId}&playerId=${playerId}`, {
        method: 'GET'
      }, this.CLOUD_TIMEOUT_MS);
      logger.debug(`[CloudSaveManager] Loaded save data for game "${gameId}" for player "${playerId}" from cloud`);
      
      const result = !response || !response.savedGameData ? null : {
        savedGameData: response.savedGameData,
        timestamp: response.timestamp || 0,
        source: this.id,
        deviceId: response.deviceId, // Include deviceId from cloud save
      };

      // Cache the result
      this.loadCache.set(cacheKey, { savedGameData: result, timestamp: Date.now() });

      return result;
    } catch (error) {
      logger.error('[CloudSaveManager] Failed to load from cloud:', error);
      return null;
    }
  }

  /**
   * Cloud save has higher priority than local (10 > 0)
   */
  getPriority(): number {
    return 10;
  }
}
